import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Api } from '@lentau/api/services/monolith/api.service';
import { ResponseErrorListItemProps } from '@lentau/api/shared/monolith/types';
import { AuthService, SendAuthCodeRequestParams } from 'angular-api-gateway/v1/api/auth.service';
import { SendAuthCodeResponse } from 'angular-api-gateway/v1/model/sendAuthCodeResponse';
import { SendAuthCodeRequest } from 'angular-api-gateway/v1/model/sendAuthCodeRequest';
import { SocialAuthStart200Response } from 'angular-api-gateway/v1/model/socialAuthStart200Response';
import { SocialAuthStartRequest } from 'angular-api-gateway/v1/model/socialAuthStartRequest';
import { SocialAuthValidate200Response } from 'angular-api-gateway/v1/model/socialAuthValidate200Response';
import { SocialAuthValidateRequest } from 'angular-api-gateway/v1/model/socialAuthValidateRequest';
import { defaultApiGatewayParams, GatewayPlatforms } from '@lentau/core/api/gateway';
import { DeviceIdService } from '@lentau/core/api/shared/device-id.service';
import { HttpHeaders } from '@angular/common/http';
import { AuthResult, ResponseAuthLogout, ResponseGetAuthTokenDto } from '@lentau/auth/shared';
import { mapTabletType, ReferralData, TABLET_SETTINGS, TabletSettings, TabletsOrigin } from './const';
import { LOCAL_STORAGE } from '@lentau/ui-kit/core/tokens/local-storage';
import { Router } from '@angular/router';
import { VerifyAuthCodeRequest } from 'angular-api-gateway/v1/model/verifyAuthCodeRequest';

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private localStorage: Storage = inject(LOCAL_STORAGE);
  private router: Router = inject(Router);
  private api: Api = inject(Api);
  private authService: AuthService = inject(AuthService);
  private deviceIdService: DeviceIdService = inject(DeviceIdService);

  public authCodeSend(
    phone: string,
    acceptMarketingCommunications: boolean = true,
    isTablet: boolean = false
  ): Observable<SendAuthCodeResponse> {
    const body: SendAuthCodeRequest = {
      phone,
      acceptMarketingCommunications: acceptMarketingCommunications,
    };

    const params: SendAuthCodeRequestParams = isTablet
      ? { ...defaultApiGatewayParams, xPlatform: this.getTabletPlatform(), sendAuthCodeRequest: body }
      : { ...defaultApiGatewayParams, sendAuthCodeRequest: body };

    return this.authService
      .sendAuthCode(params)
      .pipe(catchError((error: ResponseErrorListItemProps[]) => this.handleFailureResponse(error)));
  }

  public authCodeTokenGet(
    phone: string,
    code: string,
    isTablet: boolean = false,
    isReferral: boolean = false
  ): Observable<AuthResult> {
    let body: VerifyAuthCodeRequest = { phone, code };

    if (isTablet) {
      body = { ...body, origin: this.getTabletOrigin() };
    }

    if (isReferral) {
      body = { ...body, referral: this.getReferralData() };
    }

    return this.authService.verifyAuthCode({ ...defaultApiGatewayParams, verifyAuthCodeRequest: body });
  }

  public socialAuthStart(provider: SocialAuthStartRequest['provider']): Observable<SocialAuthStart200Response> {
    return this.authService.socialAuthStart({
      ...defaultApiGatewayParams,
      deviceId: this.deviceIdService.getDeviceId(),
      socialAuthStartRequest: { provider },
    });
  }

  public socialAuthValidate(
    socialAuthValidateRequest: SocialAuthValidateRequest
  ): Observable<SocialAuthValidate200Response> {
    return this.authService.socialAuthValidate({
      ...defaultApiGatewayParams,
      deviceId: this.deviceIdService.getDeviceId(),
      socialAuthValidateRequest,
    });
  }

  private handleFailureResponse(error: ResponseErrorListItemProps[]): Observable<never> {
    return throwError(() => [
      {
        Code: error[0]?.Code,
        Message: error[0]?.Description || error[0]?.Message,
      },
    ]);
  }

  public logout(): Observable<ResponseAuthLogout> {
    return this.api.post('userSessionDelete');
  }

  public sessionGet(SessionToken: string): Observable<ResponseGetAuthTokenDto> {
    const head: { SessionToken: string } = { SessionToken };
    return this.api.post('sessionGet', {}, head);
  }

  private getHeaderForApiGatewayRequest(isTablet: boolean = false): HttpHeaders {
    const headers: HttpHeaders = new HttpHeaders();
    if (isTablet) {
      return headers
        .set('X-Platform', this.getTabletPlatform())
        .set('X-Device-OS', 'Web')
        .set('X-Device-OS-Version', '12.4.8');
    } else {
      return headers.set('X-Platform', 'omniweb').set('X-Device-OS', 'Web').set('X-Device-OS-Version', '12.4.8');
    }
  }

  private getTabletPlatform(): GatewayPlatforms {
    const data: string = this.localStorage.getItem(TABLET_SETTINGS);
    if (data === null) {
      return mapTabletType.get(0);
    }
    const tabletData: TabletSettings = JSON.parse(data);
    return mapTabletType.get(tabletData.type);
  }

  private getTabletOrigin(): TabletsOrigin {
    const data: string = this.localStorage.getItem(TABLET_SETTINGS);
    const origin: TabletsOrigin = {
      shop: '0002',
      createdBy: 'Фамилия Имя Отчество',
    };
    if (data === null) {
      return origin;
    }
    const tabletData: TabletSettings = JSON.parse(data);
    origin.createdBy =
      (tabletData.secondName ?? 'Фамилия') +
      ' ' +
      (tabletData.name ?? 'Имя') +
      ' ' +
      (tabletData.thirdName ?? 'Отчество');
    origin.shop = tabletData.tkId ?? '0002';
    return origin;
  }

  private getReferralData(): ReferralData {
    return {
      campaignId: this.getReferralCampaignIdFromRouterUrl(this.router.url),
      externalId: this.getReferralExternalIdFromRouterUrl(this.router.url),
    };
  }

  private getReferralCampaignIdFromRouterUrl(rawUrl: string): string {
    const pathOnly: string = rawUrl.split('?', 1)[0] ?? '';
    const segments: string[] = pathOnly.split('/').filter((segment: string) => segment.length > 0);

    return segments[1] ?? '';
  }

  private getReferralExternalIdFromRouterUrl(rawUrl: string): string {
    const queryStart: number = rawUrl.indexOf('?');
    if (queryStart === -1) {
      return '';
    }
    const searchParams: URLSearchParams = new URLSearchParams(rawUrl.slice(queryStart + 1));

    return searchParams.get('utm_content') ?? '';
  }
}
